/**
 * Den marketing site — a multi-page, fully responsive experience.
 *
 * Visual language: a bright, light institutional surface (Utila-inspired hero,
 * Privy-inspired content sections) in a cool-neutral palette with near-black
 * type and buttons and a sparing warm Den accent. The shared <Layout> provides
 * the mega-menu header and footer; copy lives in src/content.
 */

import { Routes, Route } from 'react-router';
import { lazy, Suspense } from 'react';

import { Layout } from '@/components/Layout';
import { ScrollToTop } from '@/components/ScrollToTop';
import { ExternalRedirect } from '@/components/ExternalRedirect';
import { Home } from '@/pages/Home';
import { LegalPage } from '@/pages/LegalPage';
import { MarketingPage } from '@/pages/MarketingPage';
import { NotFound } from '@/pages/NotFound';
import { Blog } from '@/pages/Blog';
import { GetStarted } from '@/pages/GetStarted';
import { adCampaigns } from '@/content/get-started';
import { redirects } from './routes';
import {
  mlsWalletTermsDoc,
  otherOfferingsTermsDoc,
  privacyDoc,
} from '@/content/legal';

const Article = lazy(() =>
  import('@/pages/Article').then((module) => ({ default: module.Article }))
);

function App() {
  return (
    <>
      <ScrollToTop />
      <Routes>
        {adCampaigns.map((campaign) => (
          <Route
            key={campaign.id}
            path={campaign.path}
            element={<GetStarted key={campaign.id} campaign={campaign} />}
          />
        ))}
        {redirects.flatMap(({ paths, to }) =>
          paths.map((path) => (
            <Route
              key={path}
              path={path}
              element={<ExternalRedirect to={to} />}
            />
          ))
        )}
        <Route element={<Layout />}>
          <Route path="/" element={<Home />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/category/:category" element={<Blog />} />
          <Route
            path="/blog/:slug"
            element={
              <Suspense
                fallback={
                  <div
                    className="min-h-screen px-6 pt-36 text-ink-500"
                    role="status"
                  >
                    Loading article…
                  </div>
                }
              >
                <Article />
              </Suspense>
            }
          />
          <Route path="/product/:slug" element={<MarketingPage />} />
          <Route path="/security/:slug" element={<MarketingPage />} />
          <Route path="/solutions/:slug" element={<MarketingPage />} />
          <Route
            path="/terms"
            element={
              <LegalPage
                doc={{ title: 'Terms of Use', intro: [], sections: [] }}
                tabs={[
                  {
                    id: 'mls-wallet',
                    label: 'MLS Wallet',
                    doc: mlsWalletTermsDoc,
                  },
                  {
                    id: 'other-offerings',
                    label: 'Other Offerings',
                    doc: otherOfferingsTermsDoc,
                  },
                ]}
              />
            }
          />
          <Route path="/privacy" element={<LegalPage doc={privacyDoc} />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </>
  );
}

export default App;
